/**
 * /exams — Channel 4 (formal section-broken assessments).
 *
 * Same shape as AssignmentPage but the score entry grid carries 6
 * section columns (L/R/W/S/V/G) instead of one. Sections with null
 * max_* are hidden on the grid (the exam didn't test that section).
 *
 * Saving a row triggers the backend profiler with weight 0.5 — exam
 * scores dominate the english_profile blend.
 */
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useMyEmployees } from "../workbench/EmployeeSwitcher";

import {
  useClassWithRoster,
  useClasses,
  useCreateExam,
  useDeleteExam,
  useExamEngineStatus,
  useExams,
  useSubmitExamScores,
  type Exam,
  type ExamScoreEntry,
  type ExamSection,
  type ExamType,
  type ExamVisionCandidate,
  type Student,
} from "../../api/school";
import { ExamScoreImageUpload } from "../../components/ExamScoreImageUpload";

const EXAM_TYPES: ExamType[] = ["unit_test", "quiz", "midterm", "final", "mock", "standardized"];

const SECTION_KEYS = [
  "listening",
  "reading",
  "writing",
  "speaking",
  "vocabulary",
  "grammar",
] as const;

type SectionKey = (typeof SECTION_KEYS)[number];

export function ExamPage() {
  const { t } = useTranslation();
  const classes = useClasses();
  // A5: the teaching-plan 考试/试学 CTA deep-links here with
  // ?create=1&class_id=…&title=… so the form opens pre-filled.
  const [params] = useSearchParams();
  const [classId, setClassId] = useState(params.get("class_id") ?? "");
  const effective = classId || classes.data?.[0]?.id || "";
  const classDetail = useClassWithRoster(effective);
  const exams = useExams(effective);
  const engineStatus = useExamEngineStatus(effective);
  const deleteExam = useDeleteExam();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(params.get("create") === "1");

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6 flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold text-slate-800">{t("exams.title")}</h1>
        {classes.data && classes.data.length === 0 && (
          <span className="text-xs text-amber-600">{t("common.noClassAssigned")}</span>
        )}
        <select
          value={effective}
          onChange={(e) => {
            setClassId(e.target.value);
            setExpandedId(null);
            setShowCreate(false);
          }}
          className="px-3 py-1.5 rounded border border-slate-200 text-sm"
        >
          {classes.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </header>

      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={() => setShowCreate((v) => !v)}
          className="px-3 py-1.5 rounded bg-sky-500 text-white text-sm font-medium hover:bg-sky-600"
        >
          {showCreate ? t("exams.cancel") : t("exams.newExam")}
        </button>
      </div>

      {showCreate && classDetail.data && (
        <CreateExamForm
          schoolId={classDetail.data.school_id}
          classId={effective}
          initialTitle={params.get("title") ?? ""}
          onCreated={() => setShowCreate(false)}
        />
      )}

      {exams.isLoading && <p className="text-slate-500 italic">{t("common.loading")}</p>}
      {!exams.isLoading && (exams.data?.length ?? 0) === 0 && !showCreate && (
        <div className="rounded border border-slate-200 bg-white p-8 text-center text-slate-500">
          {t("exams.empty")}
        </div>
      )}

      <ul className="space-y-3">
        {exams.data?.map((ex) => (
          <li key={ex.id} className="relative rounded-lg border border-slate-200 bg-white">
            <button
              type="button"
              onClick={() => setExpandedId((id) => (id === ex.id ? null : ex.id))}
              className="w-full text-left p-4 hover:bg-slate-50 flex items-center justify-between"
            >
              <div>
                <div className="text-base font-medium text-slate-800">{ex.title}</div>
                <div className="text-xs text-slate-500 mt-1 flex gap-3 flex-wrap">
                  <span>{t(`exams.types.${ex.type}`)}</span>
                  <span>{t("exams.row.date", { date: ex.exam_date })}</span>
                  <span>{t("exams.row.maxTotal", { max: ex.max_total })}</span>
                  <span>
                    {t("exams.row.sectionsLabel", {
                      sections: (() => {
                        // Prefer the Jiangning-shape sections JSONB; fall back
                        // to the legacy fixed-column flags for older exams.
                        if (ex.sections && ex.sections.length > 0) {
                          return ex.sections
                            .filter((s) => s.max_score > 0)
                            .map((s) => s.name)
                            .join(" / ");
                        }
                        const legacy = SECTION_KEYS.filter(
                          (k) => ex[`max_${k}` as keyof typeof ex] != null,
                        )
                          .map((k) => t(`exams.sections.${k}`))
                          .join(" / ");
                        return legacy || t("exams.row.sectionsEmpty");
                      })(),
                    })}
                  </span>
                </div>
              </div>
              <span className="text-xs text-sky-600">
                {expandedId === ex.id ? t("exams.row.collapse") : t("exams.row.enterScores")}
              </span>
            </button>
            <button
              type="button"
              title={t("exams.row.delete")}
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(t("exams.row.deleteConfirm", { title: ex.title }))) {
                  deleteExam.mutate(ex.id);
                }
              }}
              className="absolute right-3 top-3 rounded border border-slate-200 px-1.5 py-0.5 text-xs text-slate-400 hover:border-rose-300 hover:text-rose-600"
            >
              🗑
            </button>
            <PipelineChips examId={ex.id} status={engineStatus.data?.[ex.id]} />
            {expandedId === ex.id && classDetail.data && (
              <div className="border-t border-slate-100 p-4 bg-slate-50">
                <ExamScoreGrid
                  exam={ex}
                  students={classDetail.data.students}
                />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Engine pipeline chips: 蓝图 → 组卷 → 印卷 → 录分 ──────────────────────

function PipelineChips({
  examId,
  status,
}: {
  examId: string;
  status?: import("../../api/school").ExamEngineStatus;
}) {
  const { t } = useTranslation();
  if (!status) return null;
  const steps: { key: string; to: string; state: "done" | "active" | "todo"; label: string }[] = [
    {
      key: "blueprint",
      to: `/exams/${examId}/blueprint`,
      state: status.blueprint === "approved" ? "done" : status.blueprint === "draft" ? "active" : "todo",
      label: t("exams.pipeline.blueprint"),
    },
    {
      key: "paper",
      to: `/exams/${examId}/paper`,
      state:
        status.paper === "finalized" || status.paper === "printed"
          ? "done"
          : status.paper !== "none"
            ? "active"
            : "todo",
      label: t("exams.pipeline.paper"),
    },
    {
      key: "print",
      to: `/exams/${examId}/paper`,
      state: status.printed ? "done" : "todo",
      label: t("exams.pipeline.print"),
    },
    {
      key: "scores",
      to: `/exams/${examId}/scores`,
      state: status.scored > 0 ? "done" : "todo",
      label:
        status.scored > 0
          ? t("exams.pipeline.scored", { n: status.scored })
          : t("exams.pipeline.scores"),
    },
  ];
  const cls = {
    done: "border-emerald-200 bg-emerald-50 text-emerald-700",
    active: "border-amber-200 bg-amber-50 text-amber-700",
    todo: "border-slate-200 bg-white text-slate-400",
  } as const;
  return (
    <div className="flex items-center gap-1 px-4 pb-3 -mt-1 text-xs">
      {steps.map((s, i) => (
        <span key={s.key} className="flex items-center gap-1">
          {i > 0 && <span className="text-slate-300">→</span>}
          <Link to={s.to} className={`px-2 py-0.5 rounded-full border hover:opacity-80 ${cls[s.state]}`}>
            {s.state === "done" ? "✓ " : ""}
            {s.label}
          </Link>
        </span>
      ))}
    </div>
  );
}

// ── Create form ────────────────────────────────────────────────────────────

function CreateExamForm({
  schoolId,
  classId,
  initialTitle = "",
  onCreated,
}: {
  schoolId: string;
  classId: string;
  initialTitle?: string;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  // Blueprint-first (spec §6.1): default mode asks only title/type/date/
  // max_total — the section structure comes from the approved blueprint,
  // not from up-front guesses. Manual mode keeps the 6 fixed section
  // maxes for externally-scored exams (Jiangning-style paper results).
  const [mode, setMode] = useState<"engine" | "manual">("engine");
  const [title, setTitle] = useState(initialTitle);
  const [type, setType] = useState<ExamType>("unit_test");
  const [date, setDate] = useState(today);
  const [maxTotal, setMaxTotal] = useState("100");
  const [maxes, setMaxes] = useState<Record<SectionKey, string>>({
    listening: "25",
    reading: "40",
    writing: "25",
    speaking: "0",
    vocabulary: "15",
    grammar: "20",
  });
  const create = useCreateExam();
  // 学科标签(Jarod 2026-08-14):考试归属学科由老师显式选择——
  // 单学科自动带入;多学科(如管理员/全科账号)出下拉,默认第一个。
  const { employees } = useMyEmployees();
  const [subjectId, setSubjectId] = useState<string>("");
  const effectiveSubjectId = subjectId || (employees[0]?.subject_id ?? "");

  const sectionTotal = useMemo(
    () =>
      Object.values(maxes).reduce((acc, v) => acc + (Number(v) || 0), 0),
    [maxes],
  );
  const total = mode === "engine" ? Number(maxTotal) || 0 : sectionTotal;

  async function submit() {
    if (!title.trim() || total <= 0) return;
    const exam = await create.mutateAsync({
      school_id: schoolId,
      class_id: classId,
      title: title.trim(),
      type,
      subject_id: effectiveSubjectId || null,
      exam_date: date,
      max_listening: mode === "manual" ? Number(maxes.listening) || null : null,
      max_reading: mode === "manual" ? Number(maxes.reading) || null : null,
      max_writing: mode === "manual" ? Number(maxes.writing) || null : null,
      max_speaking: mode === "manual" ? Number(maxes.speaking) || null : null,
      max_vocabulary: mode === "manual" ? Number(maxes.vocabulary) || null : null,
      max_grammar: mode === "manual" ? Number(maxes.grammar) || null : null,
      max_total: total,
    });
    setTitle("");
    onCreated();
    if (mode === "engine") navigate(`/exams/${exam.id}/blueprint`);
  }

  return (
    <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 p-4">
      {employees.length > 1 && (
        <label className="mb-2 flex items-center gap-2 text-sm">
          <span className="text-xs text-slate-600">{t("exams.create.subjectLabel")}</span>
          <select
            value={effectiveSubjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          >
            {employees.map((emp: { subject_id: string; name: string }) => (
              <option key={emp.subject_id} value={emp.subject_id}>{emp.name}</option>
            ))}
          </select>
        </label>
      )}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">{t("exams.create.header")}</h3>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs">
          {(["engine", "manual"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`px-3 py-1 ${
                mode === m ? "bg-sky-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {t(`exams.create.mode.${m}`)}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-3">{t(`exams.create.modeHint.${mode}`)}</p>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <label className="col-span-2 text-sm">
          <span className="block text-xs text-slate-600 mb-1">{t("exams.create.titleLabel")}</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("exams.create.titlePlaceholder")}
            className="w-full px-2 py-1 rounded border border-slate-200"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-slate-600 mb-1">{t("exams.create.typeLabel")}</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as ExamType)}
            className="w-full px-2 py-1 rounded border border-slate-200"
          >
            {EXAM_TYPES.map((typ) => (
              <option key={typ} value={typ}>
                {t(`exams.types.${typ}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs text-slate-600 mb-1">{t("exams.create.dateLabel")}</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-2 py-1 rounded border border-slate-200 w-full"
          />
        </label>
        {mode === "engine" && (
          <label className="text-sm">
            <span className="block text-xs text-slate-600 mb-1">{t("exams.create.maxTotalLabel")}</span>
            <input
              type="number"
              min={1}
              value={maxTotal}
              onChange={(e) => setMaxTotal(e.target.value)}
              className="px-2 py-1 rounded border border-slate-200 w-full"
            />
          </label>
        )}
      </div>
      {mode === "manual" && (
        <div className="mb-3">
          <div className="text-xs text-slate-600 mb-1">
            {t("exams.create.sectionMaxHint")}
          </div>
          <div className="grid grid-cols-6 gap-2">
            {SECTION_KEYS.map((k) => (
              <label key={k} className="text-xs">
                <span className="block text-slate-600 mb-1">{t(`exams.sections.${k}`)}</span>
                <input
                  type="number"
                  min={0}
                  value={maxes[k]}
                  onChange={(e) =>
                    setMaxes((p) => ({ ...p, [k]: e.target.value }))
                  }
                  className="w-full px-2 py-1 rounded border border-slate-200"
                />
              </label>
            ))}
          </div>
          <div className="mt-2 text-xs text-slate-500">
            {t("exams.create.totalAuto")}
            <span className="font-mono text-slate-800 ml-1">{total}</span>
          </div>
        </div>
      )}
      <div className="flex items-center gap-3 justify-end">
        {create.error && (
          <span className="text-xs text-red-600">{(create.error as Error).message}</span>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={!title.trim() || total <= 0 || create.isPending}
          className="px-3 py-1.5 rounded bg-sky-500 text-white text-sm hover:bg-sky-600 disabled:opacity-50"
        >
          {create.isPending
            ? t("exams.create.submitting")
            : mode === "engine"
              ? t("exams.create.submitEngine")
              : t("exams.create.submit")}
        </button>
      </div>
    </div>
  );
}

// ── Section-aware score grid (one row per student × N section columns) ───

/** Build the active section list. Prefer `exam.sections` JSONB (Jiangning
 *  shape, N sections). Fall back to the legacy 6-column max_<section>
 *  fields for older exams created with the simple create-form path. */
function deriveActiveSections(exam: Exam, sectionLabel: (k: SectionKey) => string): ExamSection[] {
  if (exam.sections && exam.sections.length > 0) {
    return exam.sections.filter((s) => s.max_score > 0);
  }
  const legacy: ExamSection[] = [];
  const fields: Array<{ key: SectionKey; max: number | null }> = [
    { key: "listening", max: exam.max_listening },
    { key: "reading", max: exam.max_reading },
    { key: "writing", max: exam.max_writing },
    { key: "speaking", max: exam.max_speaking },
    { key: "vocabulary", max: exam.max_vocabulary },
    { key: "grammar", max: exam.max_grammar },
  ];
  for (const f of fields) {
    if (f.max && f.max > 0) {
      legacy.push({ name: sectionLabel(f.key), name_en: f.key, max_score: f.max });
    }
  }
  return legacy;
}

function ExamScoreGrid({
  exam,
  students,
}: {
  exam: Exam;
  students: Student[];
}) {
  const { t } = useTranslation();
  const sectionLabel = (k: SectionKey) => t(`exams.sections.${k}`);
  const activeSections = deriveActiveSections(exam, sectionLabel);

  type RowState = Record<string, Record<string, string>>;
  const [values, setValues] = useState<RowState>(() =>
    Object.fromEntries(
      students.map((s) => [
        s.id,
        Object.fromEntries(activeSections.map((sec) => [sec.name_en, ""])),
      ]),
    ),
  );
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const submit = useSubmitExamScores(exam.id);

  useEffect(() => {
    setValues(
      Object.fromEntries(
        students.map((s) => [
          s.id,
          Object.fromEntries(activeSections.map((sec) => [sec.name_en, ""])),
        ]),
      ),
    );
  }, [students, exam.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function studentTotal(studentId: string): number {
    return activeSections.reduce((acc, sec) => {
      const raw = values[studentId]?.[sec.name_en]?.trim() ?? "";
      const n = Number(raw);
      return acc + (Number.isFinite(n) ? n : 0);
    }, 0);
  }

  async function save() {
    setErr(null);
    setSaving(true);
    try {
      const rows: ExamScoreEntry[] = [];
      for (const s of students) {
        const sectionScores: Record<string, number> = {};
        let anyFilled = false;
        let total = 0;
        for (const sec of activeSections) {
          const raw = values[s.id]?.[sec.name_en]?.trim() ?? "";
          if (raw === "") continue;
          const n = Number(raw);
          if (Number.isNaN(n))
            throw new Error(
              t("exams.grid.errNotNumber", { name: s.name_zh, section: sec.name }),
            );
          if (n < 0 || n > sec.max_score)
            throw new Error(
              t("exams.grid.errOutOfRange", {
                name: s.name_zh,
                section: sec.name,
                score: n,
                max: sec.max_score,
              }),
            );
          sectionScores[sec.name_en] = n;
          total += n;
          anyFilled = true;
        }
        if (!anyFilled) continue;
        rows.push({
          student_id: s.id,
          section_scores: sectionScores,
          total,
        });
      }
      if (rows.length === 0) throw new Error(t("exams.grid.errNoScores"));
      await submit.mutateAsync(rows);
      setSavedAt(Date.now());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function applyCandidates(candidates: ExamVisionCandidate[]) {
    setValues((prev) => {
      const next = { ...prev };
      for (const c of candidates) {
        if (!next[c.student_id]) continue;
        const row: Record<string, string> = { ...next[c.student_id] };
        for (const sec of activeSections) {
          const v = c.section_scores?.[sec.name_en];
          if (v !== undefined && v !== null) {
            row[sec.name_en] = String(v);
          }
        }
        next[c.student_id] = row;
      }
      return next;
    });
  }

  return (
    <>
      <ExamScoreImageUpload
        examId={exam.id}
        examTitle={exam.title}
        sections={activeSections}
        students={students}
        onCandidatesExtracted={applyCandidates}
      />
    <div className="rounded border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-3 py-2 font-medium w-32">{t("exams.grid.studentHeader")}</th>
            {activeSections.map((s) => (
              <th key={s.name_en} className="text-left px-3 py-2 font-medium whitespace-nowrap">
                {s.name} / {s.max_score}
              </th>
            ))}
            <th className="text-right px-3 py-2 font-medium text-slate-500 w-20">{t("exams.grid.totalHeader")}</th>
          </tr>
        </thead>
        <tbody>
          {students.map((s) => (
            <tr key={s.id} className="border-t border-slate-100">
              <td className="px-3 py-2 text-slate-800 whitespace-nowrap">
                {s.name_zh}
              </td>
              {activeSections.map((sec) => (
                <td key={sec.name_en} className="px-3 py-2">
                  <input
                    type="number"
                    min={0}
                    max={sec.max_score}
                    className="w-20 px-2 py-1 rounded border border-slate-200 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-200"
                    value={values[s.id]?.[sec.name_en] ?? ""}
                    placeholder={t("exams.grid.emptyCell")}
                    disabled={saving}
                    onChange={(e) =>
                      setValues((prev) => ({
                        ...prev,
                        [s.id]: { ...prev[s.id], [sec.name_en]: e.target.value },
                      }))
                    }
                  />
                </td>
              ))}
              <td className="px-3 py-2 text-right font-mono text-slate-700">
                {studentTotal(s.id) || t("exams.grid.emptyCell")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between">
        <div className="text-xs">
          {err ? (
            <span className="text-red-600">{err}</span>
          ) : savedAt ? (
            <span className="text-emerald-600">{t("exams.grid.savedNote")}</span>
          ) : (
            <span className="text-slate-500">{t("exams.grid.blankHint")}</span>
          )}
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-4 py-1.5 rounded bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 disabled:opacity-50"
        >
          {saving ? t("exams.grid.saving") : t("exams.grid.saveAll")}
        </button>
      </div>
    </div>
    </>
  );
}
